import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import browser from 'browser-detect';
import {
  getToken,
  incrementRejectedTokenAttempts,
  isAuthorized,
  isMaxRejectedAttemptsEcxeeded,
  removeTokenDetails,
  resetRejectedTokenAttempts
} from './auth';
import { reload } from './browser';
import { APP_VERSION, ENV, MOT_GW_API_BASE_URL } from './constants';
import '../axios.d';
import { publishEventEffect } from './analytika/reporterAnalytika';
import { genericNetworkErrorSample, replaceAuthInfo } from './analytika/events';
import publishAV2Event from './analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from './analytika/enums';

export const retryConfig = {
  retries: 3,
  retryDelay: (retryCount: number) => {
    return retryCount * 2000;
  },
  retryCondition: () => true
};

export const NETWORK_ERROR = 'Network Error';

export interface RequestOptions {
  url?: string;
  method?: string;
  params?: any;
  headers?: any;
  data?: any;
}

export class NetworkError extends Error {
  request: RequestOptions;
  isNetworkOnline: boolean;

  constructor(request: RequestOptions) {
    const msg = `Network error: ${request.url}`;
    super(msg);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }

    this.name = msg;
    this.request = request;
    this.isNetworkOnline = !!window.navigator.onLine;
  }
}

export function authRequestInterceptor(request: AxiosRequestConfig) {
  request.headers['Authorization'] = `Bearer ${getToken()}`;
  return request;
}

export function errorResponseInterceptor(error: AxiosError) {
  const { response, config, message, code } = error;
  // @ts-ignore
  publishEventEffect(genericNetworkErrorSample(error), { user: { userSettings: {}, userData: {} } });

  publishAV2Event({
    event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_HTTP_REQ_FAIL,
    error_message: message,
    event_version: 2,
    path: config?.url,
    method: config?.method,
    status_code: Number(code) || response?.status,
    value: replaceAuthInfo(JSON.stringify(error))
  });

  if (error.message === NETWORK_ERROR) {
    const { config } = error;
    const { url, data, headers, params, method } = config || { url: '', data: 'NO DATA IN AXIOS CONFIG' };
    return Promise.reject(new NetworkError({ url, data, headers, params, method }));
  }

  if (response?.data) {
    // eslint-disable-next-line no-console
    console.error('network error:', response.data?.message);
  }

  if (response?.status && response.status === 401) {
    if (!isAuthorized()) {
      return reload();
    } else {
      incrementRejectedTokenAttempts();
      if (isMaxRejectedAttemptsEcxeeded()) {
        removeTokenDetails();
        resetRejectedTokenAttempts();
        return reload();
      }
      // eslint-disable-next-line no-console
      console.error('Service responded: Unauthorized');
    }
  }

  return Promise.reject(error);
}

const browserInfo = browser();

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: MOT_GW_API_BASE_URL,
  headers: {
    Application: 'careemfood-mobile-v1',
    Lat: '0',
    Lng: '0',
    /*
    Don't use `ios;production;12.1.0 (247);12.1;x86_64` because it gives less results during /addresses/search :D
    and gives addresses not close to the given pickup location Lat & Lng
    // Meta: 'android;staging;13.4.0 (210130400);10;Pixel 2 XL',
    */
    'Accept-Language': 'en-us',
    UUID: '0F2C1018-AEE9-408B-B547-C5440B3353A7',
    'Time-Zone': 'UTC',
    'Content-Type': 'application/json',
    Meta: [
      'web', // platform
      ENV,
      APP_VERSION,
      browserInfo.versionNumber,
      browserInfo.name
    ].join(';')
  }
};

export const createAPIClient = (configs: AxiosRequestConfig) => {
  const apiClient = axios.create(configs);

  apiClient.interceptors.request.use(authRequestInterceptor);
  apiClient.interceptors.response.use(res => res.data, errorResponseInterceptor);

  return apiClient;
};

export const createAPIClientWithRetry = (configs: AxiosRequestConfig) => {
  const apiClient = axios.create(configs);

  apiClient.interceptors.request.use(authRequestInterceptor);
  apiClient.interceptors.response.use(res => res.data, errorResponseInterceptor);
  axiosRetry(apiClient, retryConfig);

  return apiClient;
};

export const api = createAPIClientWithRetry(DEFAULT_CONFIG);

export const getErrorFromResponse = (error: AxiosError | Error) => {
  return (error as AxiosError).response?.data?.message || error.message;
};
